import { MANDATE_PCT_TOLERANCE } from "@repo/shared";
import type { MandateTemplateMode, ProductListItem } from "@repo/shared/types";
import { Trash2 } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./mandate-template-lines-editor.i18n";

/** A line as the dialog holds it: strings in the inputs, parsed on save. */
export type TemplateLineDraft = {
  productId: string;
  weightPct: string;
  minPct: string;
  defaultPct: string;
  maxPct: string;
};

export function emptyLineDraft(productId: string): TemplateLineDraft {
  return { productId, weightPct: "", minPct: "", defaultPct: "", maxPct: "" };
}

const toNumber = (value: string): number => {
  const n = Number.parseFloat(value.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

export function sumOf(lines: readonly TemplateLineDraft[], field: keyof TemplateLineDraft): number {
  return lines.reduce((acc, l) => acc + (field === "productId" ? 0 : toNumber(l[field])), 0);
}

type MandateTemplateLinesEditorProps = {
  mode: MandateTemplateMode;
  lines: TemplateLineDraft[];
  onChange: (lines: TemplateLineDraft[]) => void;
  /** The collection's products. The editor offers those on every selected platform. */
  products: ProductListItem[];
  /** The template's ticked platforms — a line's product must be on every one of them. */
  selectedPlatformIds: string[];
  disabled?: boolean;
};

/**
 * QL-375 — the lines of a mandate template: a searchable product picker (ISIN
 * as keyword, like the model-portfolio editor) filtered to the "common nodes"
 * of the ticked platforms, a table with the figures for the template's mode,
 * and the running totals the invariants are judged on. Units-priced products
 * are offered: product type makes no difference to a mandate.
 */
export function MandateTemplateLinesEditor({
  mode,
  lines,
  onChange,
  products,
  selectedPlatformIds,
  disabled = false,
}: MandateTemplateLinesEditorProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const label = (i18n: Record<string, string> | null | undefined) => localize(i18n ?? {});

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  // A product is offerable when it has an ISIN and is available on every
  // selected platform (untagged template → every product).
  const coversPlatforms = (p: ProductListItem) =>
    selectedPlatformIds.every((id) => p.platformIds.includes(id));

  const options = useMemo(
    () =>
      products
        .filter((p) => p.isin && coversPlatforms(p) && !lines.some((l) => l.productId === p.id))
        .map((p) => ({
          value: p.id,
          label: `${label(p.nameI18n)} (${p.isin})`,
          keywords: [p.isin ?? ""],
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, lines, selectedPlatformIds, userLanguage],
  );

  const update = (index: number, patch: Partial<TemplateLineDraft>) =>
    onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const numberInput = (index: number, field: keyof TemplateLineDraft, value: string) => (
    <Input
      type="number"
      inputMode="decimal"
      min={0}
      max={100}
      step={0.01}
      value={value}
      onChange={(e) => update(index, { [field]: e.target.value })}
      disabled={disabled}
      className="w-24 text-right"
      aria-label={`${t[field === "weightPct" ? "weight" : field === "minPct" ? "min" : field === "defaultPct" ? "default" : "max"]} ${index + 1}`}
    />
  );

  const total = mode === "fixed" ? sumOf(lines, "weightPct") : sumOf(lines, "defaultPct");
  const totalOk = Math.abs(total - 100) <= MANDATE_PCT_TOLERANCE;
  const sumMin = sumOf(lines, "minPct");
  const sumMax = sumOf(lines, "maxPct");
  const bandsOk = sumMin <= 100 + MANDATE_PCT_TOLERANCE && sumMax >= 100 - MANDATE_PCT_TOLERANCE;

  return (
    <div className="space-y-3">
      <SearchableSelect
        value=""
        onValueChange={(productId) => productId && onChange([...lines, emptyLineDraft(productId)])}
        options={options}
        placeholder={t.addLine}
        searchPlaceholder={t.searchProducts}
        emptyMessage={t.noProducts}
        noResultsMessage={t.noResults}
        disabled={disabled}
      />

      {lines.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t.noLines}</p>
      ) : (
        <Table variant="data">
          <TableHeader>
            <TableRow>
              <TableHead>{t.instrument}</TableHead>
              <TableHead>{t.isin}</TableHead>
              {mode === "fixed" ? (
                <TableHead className="text-right">{t.weight}</TableHead>
              ) : (
                <>
                  <TableHead className="text-right">{t.min}</TableHead>
                  <TableHead className="text-right">{t.default}</TableHead>
                  <TableHead className="text-right">{t.max}</TableHead>
                </>
              )}
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line, index) => {
              const product = productById.get(line.productId);
              const uncovered = product ? !coversPlatforms(product) : false;
              return (
                <TableRow key={line.productId}>
                  <TableCell className={uncovered ? "text-destructive" : undefined}>
                    <div className="font-medium">
                      {product ? label(product.nameI18n) : line.productId}
                    </div>
                    {uncovered && <div className="text-xs">{t.notOnPlatform}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {product?.isin ?? "—"}
                  </TableCell>
                  {mode === "fixed" ? (
                    <TableCell className="text-right">
                      {numberInput(index, "weightPct", line.weightPct)}
                    </TableCell>
                  ) : (
                    <>
                      <TableCell className="text-right">
                        {numberInput(index, "minPct", line.minPct)}
                      </TableCell>
                      <TableCell className="text-right">
                        {numberInput(index, "defaultPct", line.defaultPct)}
                      </TableCell>
                      <TableCell className="text-right">
                        {numberInput(index, "maxPct", line.maxPct)}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <Button
                      size="icon"
                      variant="ghost"
                      tooltip={t.remove}
                      onClick={() => onChange(lines.filter((_, i) => i !== index))}
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell className="font-medium" colSpan={2}>
                {t.total}
              </TableCell>
              {mode === "fixed" ? (
                <TableCell
                  className={`text-right font-medium ${totalOk ? "text-success" : "text-warning"}`}
                >
                  {total.toFixed(2)} %
                </TableCell>
              ) : (
                <>
                  <TableCell
                    className={`text-right text-xs ${bandsOk ? "text-muted-foreground" : "text-warning"}`}
                  >
                    {t.sumMin} {sumMin.toFixed(2)} %
                  </TableCell>
                  <TableCell
                    className={`text-right font-medium ${totalOk ? "text-success" : "text-warning"}`}
                  >
                    {total.toFixed(2)} %
                  </TableCell>
                  <TableCell
                    className={`text-right text-xs ${bandsOk ? "text-muted-foreground" : "text-warning"}`}
                  >
                    {t.sumMax} {sumMax.toFixed(2)} %
                  </TableCell>
                </>
              )}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      )}
    </div>
  );
}
